import Chip from '@material-ui/core/Chip';
import {createStyles, Grid, makeStyles, Paper, TextField, Theme} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, {useEffect, useState} from 'react';
import {ErrorMessage, useField} from 'formik';
import {AutocompleteProps} from '@material-ui/lab/Autocomplete/Autocomplete';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexWrap: 'wrap',
      listStyle: 'none',
      padding: theme.spacing(0.5),
    },
    chip: {
      margin: theme.spacing(0.5),
    },
  }),
);

type Props = Omit<AutocompleteProps<any, any, any, any>, 'options' | 'renderInput'> & {
  name: string;
  label: string;
  onEmailsChange: (updatedEmailAddresses: string[]) => void;
  initialEmails?: string[];
};

function isValidEmail(email: string): boolean {
  if (email.trim().length <= 0) return false;

  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  return emailRegex.test(email);
}

export default function EmailTextField(props: Props) {
  const { name, label, onEmailsChange, initialEmails = [], ...other } = props;
  const [, meta] = useField<string[]>(name);
  const classes = useStyles();

  const [values, setValues] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [oldValue, setOldValue] = useState<string>('');
  const [emailAddresses, setEmailAddresses] = useState<string[]>(initialEmails);

  // Filter out custom props from other to prevent them from being passed to Autocomplete
  const { ...autocompleteProps } = other;
  // @ts-ignore
  delete autocompleteProps.initialEmails;
  // @ts-ignore
  delete autocompleteProps.onEmailsChange;

  useEffect(() => {
    setInputValue(oldValue);
  }, [oldValue]);

  const deleteEmailAddress = (emailAddress: string) => () => {
    let updatedEmailAddresses = emailAddresses.filter((v) => v !== emailAddress);
    setEmailAddresses(updatedEmailAddresses);
    onEmailsChange(updatedEmailAddresses);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Paper component="ul" className={classes.root} elevation={0}>
          {emailAddresses.map((emailAddress) => {
            return (
              <li key={emailAddress}>
                <Chip variant="outlined" label={emailAddress} onDelete={deleteEmailAddress(emailAddress)} className={classes.chip} />
              </li>
            );
          })}
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          {...autocompleteProps}
          freeSolo
          multiple
          options={[]}
          value={values}
          id="additionalEmailAddresses"
          onInputChange={(_event, value) => {
            if (isValidEmail(value.trim()) && (value.indexOf(',') !== -1 || value.charAt(value.length - 1) === ' ')) {
              let updatedEmailAddresses = [value.trim().slice(0, value.length - 1), ...emailAddresses];
              setEmailAddresses(updatedEmailAddresses);
              onEmailsChange(updatedEmailAddresses);
              setInputValue('');
            } else {
              setInputValue(value);
            }
          }}
          onChange={(_event, values) => {
            for (const value of values) {
              if (isValidEmail(value.trim())) {
                let updatedEmailAddresses = [value.trim().slice(0, value.length), ...emailAddresses];
                setEmailAddresses(updatedEmailAddresses);
                onEmailsChange(updatedEmailAddresses);
                setInputValue('');
              } else {
                setValues([]);
                setOldValue(value);
              }
            }
          }}
          inputValue={inputValue}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              InputLabelProps={{
                shrink: true,
              }}
              label={label}
              error={meta?.touched && !!meta?.error}
              helperText={<ErrorMessage name={name} component={'span'} />}
              onBlur={(event) => {
                const value = event.target.value;
                if (isValidEmail(value.trim())) {
                  let updatedEmailAddresses = [value.trim().slice(0, value.length), ...emailAddresses];
                  setEmailAddresses(updatedEmailAddresses);
                  onEmailsChange(updatedEmailAddresses);
                  setInputValue('');
                }
              }}
            />
          )}
        />
      </Grid>
    </Grid>
  );
}
